import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { ExamTemplate } from 'src/app/models/exam_template.model';
import { ApiService } from 'src/app/services';

@Component({
  selector: 'app-exam-instance-create-dialog',
  templateUrl: './exam-instance-create-dialog.component.html',
  styleUrl: './exam-instance-create-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
})
export class ExamInstanceCreateDialogComponent implements OnInit {
  private readonly dialog_ref = inject(MatDialogRef<ExamInstanceCreateDialogComponent, boolean>);
  private readonly api_service = inject(ApiService);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly form_builder = inject(FormBuilder);

  readonly loading = signal(false);
  readonly exam_templates = signal<ExamTemplate[]>([]);

  readonly form = this.form_builder.group({
    exam_template_id: [null as number | null, Validators.required],
    name: [''],
  });

  ngOnInit(): void {
    this.loadExamTemplates();
  }

  save(): void {
    if (this.loading() || this.form.invalid) {
      return;
    }

    const raw = this.form.getRawValue();
    const exam_template_id = raw.exam_template_id;

    if (exam_template_id === null) {
      return;
    }

    this.loading.set(true);

    this.api_service
      .createExamInstanceFromTemplate({
        exam_template_id,
        name: raw.name?.trim() ? raw.name.trim() : null,
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (exam_instance): void => {
          this.dialog_ref.close(true);
          void this.router.navigate(['/employees', 'rrhh', 'exam-instances', exam_instance.id]);
        },
        error: (err: { error?: { error?: string } }): void => {
          this.loading.set(false);
          const message = err?.error?.error ?? 'Error al crear el examen.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  cancel(): void {
    this.dialog_ref.close(false);
  }

  private loadExamTemplates(): void {
    this.api_service
      .getExamTemplates({ per_page: 500, order_by: 'name:asc' })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<ExamTemplate> | ExamTemplate[]): void => {
          this.exam_templates.set(Array.isArray(response) ? response : response.data);
        },
      });
  }
}
